import { Control, useController } from 'react-hook-form';

import { CurrencyCodeEnum } from '@electricjs/core_entity-client';
import {
  Flex,
  FieldHelperText,
  spaces,
  Box,
  TextInput,
  Select,
  FieldLabel,
  Field,
} from '@electricjs/arc';

import { getCurrencyCodeOptions } from '@/components/Assets/helpers/transformOptions';

export type Price = {
  amount?: string;
  currencyCode: CurrencyCodeEnum;
};

const options = getCurrencyCodeOptions();

type PriceFieldProps = {
  id: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any, any>;
  /** value/onChange overrides, when the field can't be directly connected to the form data */
  overrides?: {
    value: Price;
    onChange: (price: Price) => void;
  };
  label?: string;
  disabled?: boolean;
};

export const PriceField = ({
  id,
  name,
  control,
  overrides,
  label,
  disabled,
}: PriceFieldProps) => {
  const { field, fieldState } = useController({
    name,
    control,
  });

  return (
    <Field width="100%">
      {label && (
        <FieldLabel id={`${id}-label`} htmlFor={id}>
          {label}
        </FieldLabel>
      )}
      <Flex width="100%" alignItems="center" columnGap={spaces.space_xs}>
        <TextInput
          id={`${id}-amount`}
          value={overrides ? overrides.value.amount : field.value.amount}
          onChange={e => {
            if (overrides) {
              overrides.onChange({
                ...overrides.value,
                amount: e.target.value,
              });
            } else {
              field.onChange({
                ...field.value,
                amount: e.target.value,
              });
            }
          }}
          onBlur={field.onBlur}
          disabled={disabled}
        />
        <Box flexBasis="100%" mb="4px">
          <Select
            id={`${id}-currency-code`}
            options={options}
            value={options.find(
              option =>
                option.value ===
                (overrides
                  ? overrides.value.currencyCode
                  : field.value.currencyCode)
            )}
            onChange={option => {
              if (overrides) {
                overrides.onChange({
                  ...overrides.value,
                  currencyCode: option?.value ?? 'USD',
                });
              } else {
                field.onChange({
                  ...field.value,
                  currencyCode: option?.value,
                });
              }
            }}
            disabled={disabled}
          />
        </Box>
      </Flex>
      {fieldState.error && (
        <FieldHelperText error errorMessage={fieldState.error.message} />
      )}
    </Field>
  );
};
